/**
 * Permission utility functions
 * 
 * Helper functions for checking user permissions in React components.
 * When "View as role" is active, effective permissions come from the simulated role.
 */

import { getUser } from '../services/auth';
import { getSimulate } from '../services/simulate';

/**
 * Normalize API permission lists to string codes (handles rare non-string shapes).
 */
export function normalizePermissionCodes(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((p) => (typeof p === 'string' ? p : p?.code))
    .filter((c) => c != null && String(c).length > 0);
}

/**
 * Effective permissions: simulated role/user if "View as" is on, else real user's.
 *
 * When simulating (`userId` or `roleId` set), we **never** fall back to the real user's
 * permission list — that hid permission bugs (e.g. admin "viewing as" SC still saw admin UI).
 * Until whoami fills `sim.permissions`, use [] (restrictive). Empty array after load means
 * the simulated identity truly has no permissions.
 */
export function getEffectivePermissions() {
  const sim = getSimulate();
  if (sim?.userId != null || sim?.roleId != null) {
    return Array.isArray(sim.permissions) ? sim.permissions : [];
  }
  const userData = getUser();
  return userData?.permissions || [];
}

/**
 * Real user permissions only (ignores simulation). Use for "can use View as" checks.
 */
export function getRealPermissions() {
  const userData = getUser();
  return userData?.permissions || [];
}

/** Permission code for View as role/user (tenant-level). */
export const VIEW_AS_PERMISSION = 'settings.view_as';

/**
 * True if the user may use View as: global developer (is_developer) or tenant permission settings.view_as.
 * Uses real permissions (not simulated).
 */
export function canUseViewAs() {
  const userData = getUser();
  if (userData?.user?.is_developer === true) return true;
  return getRealPermissions().includes(VIEW_AS_PERMISSION);
}

/**
 * True if the real user holds the global DEVELOPER (super-admin) role. Use to let support
 * agents see the full case picture (all timeline events, internal notes, assignee) even from
 * the tenant-facing Help & Support view.
 */
export function isDeveloper() {
  return getUser()?.user?.is_developer === true;
}

/** True while "View as" is simulating another role or user. */
export function isSimulating() {
  const sim = getSimulate();
  return sim?.userId != null || sim?.roleId != null;
}

/**
 * True when the requester is genuinely acting as a support agent: a real developer who is
 * NOT currently simulating another role/user via "View as". While simulating, this is false
 * so the super admin previews the true end-user experience.
 */
export function isActingAsSupportAgent() {
  return isDeveloper() && !isSimulating();
}

/** True if the user may triage the tenant's support cases (status/priority/assignment). */
export function canTriage() {
  return hasPermission('support_cases.triage');
}

/**
 * Check if user has a specific permission (uses effective = simulated when "View as" is on)
 */
export function hasPermission(permissionCode) {
  return getEffectivePermissions().includes(permissionCode);
}

/**
 * Check if user has any of the specified permissions (OR logic)
 */
export function hasAnyPermission(...permissionCodes) {
  const permissions = getEffectivePermissions();
  return permissionCodes.some(code => permissions.includes(code));
}

/**
 * Check if user has all of the specified permissions (AND logic)
 */
export function hasAllPermissions(...permissionCodes) {
  const permissions = getEffectivePermissions();
  return permissionCodes.every(code => permissions.includes(code));
}

/**
 * Get all user permissions (effective = simulated when "View as" is on)
 */
export function getUserPermissions() {
  return getEffectivePermissions();
}

/**
 * Effective role code: when "View as" is on, the simulated role's code; otherwise the real user's.
 * Used to hide UI (e.g. Central Availability) for SERVICE_COORDINATOR regardless of permission assignment.
 * Do not fall back to the real user's role while simulating — same reason as getEffectivePermissions.
 */
export function getEffectiveRoleCode() {
  const sim = getSimulate();
  if (sim?.userId != null || sim?.roleId != null) {
    return sim.roleCode ?? null;
  }
  const userData = getUser();
  return userData?.membership?.role_code ?? null;
}

/**
 * Get permission details (full objects). Empty when simulating.
 */
export function getPermissionDetails() {
  const sim = getSimulate();
  if (sim) return [];
  const userData = getUser();
  return userData?.permissionDetails || [];
}

/**
 * Legacy function for backward compatibility
 * @deprecated Use hasPermission() instead
 */
export function getPermission(permissionCode, defaultValue = false) {
  return hasPermission(permissionCode) || defaultValue;
}
